import { Button } from "@repo/ui/components/ui/button";
import { cn } from "@repo/ui/lib/utils";
import { RotateCcw } from "lucide-react";
import type { ReactNode } from "react";
import {
  EditableTimelineAddProcedure,
  EditableTimelineEnvelope,
  EditableTimelineGrid,
  EditableTimelineNotice,
  useEditableTimeline,
} from "./editable";
import { Timeline } from "./track";

/** Complete editor surface; adapters supply domain actions and their commit/reset behavior. */
export function TimelineEditor({
  renderStepProcedures,
  onReset,
  resetLabel = "Reset timing",
  resetTitle = "Restore the protocol's step placement",
  addLabel = "Add procedure",
  commit,
  untimed,
  fillHeight = false,
}: {
  renderStepProcedures?: (stepId: string) => ReactNode;
  onReset?: () => void;
  resetLabel?: string;
  resetTitle?: string;
  addLabel?: string;
  commit?: ReactNode;
  untimed?: ReactNode;
  fillHeight?: boolean;
}) {
  const {
    state: { steps },
    meta: { editable, readOnly },
  } = useEditableTimeline();
  const procedureCount = steps.reduce((count, step) => count + step.procedures.length, 0);
  return (
    <Timeline.Frame
      label={editable ? "Editable step timeline" : "Session timeline"}
      // Capped only when a bar hangs below the track: the cap exists so the
      // bar stays on screen, and a surface without one has nothing to keep
      // in view and no reason to scroll inside itself.
      className={cn(
        "isolate flex w-full min-w-0 flex-col overflow-hidden",
        fillHeight ? "min-h-0 flex-1" : !readOnly && commit && "max-h-[70vh]",
      )}
    >
      <Timeline.Header className="shrink-0 border-b bg-muted/30 px-4 py-3">
        <p className="text-xs text-muted-foreground">
          {steps.length} {steps.length === 1 ? "step" : "steps"} · {procedureCount}{" "}
          {procedureCount === 1 ? "procedure" : "procedures"}
        </p>
        {!readOnly && (
          <div className="flex flex-wrap items-center gap-2">
            <EditableTimelineEnvelope />
            <EditableTimelineAddProcedure label={addLabel} />
            {onReset && (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={resetLabel}
                title={resetTitle}
                className="size-7"
                disabled={!editable}
                onClick={onReset}
              >
                <RotateCcw className="size-4" />
              </Button>
            )}
          </div>
        )}
      </Timeline.Header>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto scrollbar-none p-4">
        <EditableTimelineNotice />
        <EditableTimelineGrid renderStepContent={renderStepProcedures} />
        {untimed}
      </div>
      {!readOnly && commit && <Timeline.Footer className="border-t bg-muted/30 px-4 py-2">{commit}</Timeline.Footer>}
    </Timeline.Frame>
  );
}
